<template>
  <popup-dialog
    :title="$t('actions.setAssistantOwners')"
    :close-button-visible="true"
    :visible="visible"
  >
    <form class="create-assistants-popup" @submit.prevent="onSubmit">
      <input-dropdown
        :options="members"
        :label="$t('actions.setOwner')"
        :value="owner"
        @update:value="onOwnerChange"
      >
        <template #default="{ item }">
          {{ item?.name }} ({{ item.email }})
        </template>
      </input-dropdown>

      <p>{{ $t("texts.ownershipTransfer") }}</p>

      <div class="label mt-5">{{ $t("actions.setAssistantOwners") }}</div>
      <users-picker
        v-if="membersWithoutOwner.length > 0"
        :users="membersWithoutOwner"
        :selected-user-ids="selectedMembers.map((o) => o.id)"
        @check-user="selectMember"
        @uncheck-user="deselectMember"
      />
      <p v-else>
        {{ $t("texts.inviteMembersFirst") }}
      </p>

      <p class="mb-0 message-notice p-4">
        {{ $t("texts.warningAssistantRights") }}
      </p>

      <button type="submit" class="button mt-5">
        {{ $t("labels.save") }}
      </button>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import UsersPicker from "../common/UsersPicker.vue";
import type { OrganizationMember } from "@/api/Organization";

const props = withDefaults(defineProps<{
  members: OrganizationMember[];
  assistantOwners: OrganizationMember[];
  owner?: OrganizationMember;
  visible?: boolean;
}>(), {
  visible: false
});

const emit = defineEmits(["save", "update:owner"]);

const selectedMembers = ref([] as OrganizationMember[]);

const membersWithoutOwner = computed(() => {
  return props.members.filter((m) => m.roleCode !== "OWNER");
});

watch(() => props.visible, () => {
  selectedMembers.value = props.assistantOwners.slice();
});

function selectMember(member: OrganizationMember) {
  selectedMembers.value.push(member);
}

function deselectMember(member: OrganizationMember) {
  const memberIndex = selectedMembers.value.findIndex(
    (m) => m.id === member.id
  );
  if (memberIndex === -1) return;
  selectedMembers.value.splice(memberIndex, 1);
}

function onSubmit() {
  emit("save", selectedMembers.value);
}

async function onOwnerChange(newOwner: OrganizationMember) {
  if (newOwner.id == props.owner?.id) {
    return;
  }
  emit("update:owner", newOwner);
}
</script>

<style lang="scss">
.create-assistants-popup {
  padding: 2rem;
}
</style>
